import { formatPhoneNumberAsYouType, parseDirectPhoneNumber } from '@opendoor/shared-ts/phone';

export const isDirectPhoneNumberValid = (value: string): boolean =>
  value.trim().length <= 40 && parseDirectPhoneNumber(value) !== undefined;

/**
 * Options for formatting currency strings
 */
export type FormatCurrencyOptions = {
  /**
   * If true, shows cents in the formatted result instead of truncating to dollars
   * @default false
   */
  showCents?: boolean;

  /**
   * If true, strips the currency sign prefix
   * @default false
   */
  stripSigil?: boolean;

  /**
   * Fallback string to return if value is not present
   * @default '-'
   */
  fallback?: string;

  /**
   * If true, treats the input as a dollar amount. Otherwise, it is treated as cents
   * @default false
   */
  inDollars?: boolean;

  /**
   * If true, inverts the sign of the value if non-null (before any formatting concerns)
   * @default false
   */
  negate?: boolean;
};

const defaultFormatCurrencyOptions: Required<FormatCurrencyOptions> = {
  showCents: false,
  stripSigil: false,
  fallback: '-',
  inDollars: false,
  negate: false,
};

const dollarsFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  roundingMode: 'trunc',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const centsFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  roundingMode: 'trunc',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formats a number representing currency as a string
 *
 * By default this function:
 *  - assumes the input is in cents
 *  - truncates cents from the resulting dollar amount
 *  - adds a currency sign prefix
 *  - falls back to the string '-' if the input value is null or undefined
 *
 * @param value - the number to format
 * @param options - {@link FormatCurrencyOptions | formatting options}
 * @returns the formatted currency string
 */
export const formatCurrency = (
  value: number | null | undefined,
  options?: Readonly<FormatCurrencyOptions>,
): string => {
  const resolvedOptions = {
    ...defaultFormatCurrencyOptions,
    ...options,
  };

  if (value === null || value === undefined) {
    return resolvedOptions.fallback;
  }

  let input = value;

  if (resolvedOptions.negate) {
    input = -input;
  }

  if (Object.is(input, -0)) {
    input = 0;
  }

  if (!resolvedOptions.inDollars) {
    input = input / 100;
  }

  let output = (resolvedOptions.showCents ? centsFormatter : dollarsFormatter).format(input);

  if (resolvedOptions.stripSigil) {
    output = output.replace('$', '');
  }

  return output;
};

export const formatCurrencyDollars = (
  value: number | null | undefined,
  options?: Readonly<Omit<FormatCurrencyOptions, 'inDollars'>>,
): string => formatCurrency(value, { ...options, inDollars: true });

/**
 * Takes in phone number format +1XXXXXXXXXX and transforms it into (XXX) XXX-XXXX
 *
 * @param rawPhoneNumber - The phone number to format
 * @returns The formatted phone number
 */
export const formatPhoneNumber = (rawPhoneNumber: string | null | undefined): string => {
  if (!rawPhoneNumber) return '';
  const cleaned = ('' + rawPhoneNumber).replace(/\D/g, '');
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[2]}) ${match[3]}-${match[4]}`;
  }
  return rawPhoneNumber;
};

export const formatPhoneNumberInput = (raw?: string | null): string =>
  formatPhoneNumberAsYouType(raw ?? '');

/**
 * Formats a value in cents for display as a currency string (without currency symbol).
 * @param cents - The value in cents, or null
 * @returns A formatted string representing the dollar value, or empty string if null
 */
export function formatCentsForDisplay(cents: number | null): string {
  if (cents === null) return '';
  const dollars = Math.round(cents / 100);
  return dollars.toLocaleString('en-US');
}

/**
 * Parses a currency input string and converts it to cents.
 * @param text - The input string, potentially containing commas and currency symbols
 * @returns The value in cents, or null if the input is empty or invalid
 */
export function parseCurrencyInputToCents(text: string): number | null {
  // Remove currency symbols, commas, and whitespace
  const cleaned = text.replace(/[$,\s]/g, '');
  if (cleaned === '') return null;

  const dollars = parseInt(cleaned, 10);
  if (isNaN(dollars)) return null;

  return dollars * 100;
}

/**
 * Converts a CAPITALIZED_SNAKE_CASE string to Title Case words.
 * e.g. "HELLO_HOW_ARE_YOU" -> "Hello How Are You"
 */
export const capitalizedSnakeCaseToWords = (str: string): string => {
  return str
    .split('_')
    .map((word) => word[0].toUpperCase() + word.substr(1).toLowerCase())
    .join(' ');
};
